import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Tabs, Tab } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserService } from './service';
import { CompanyService } from '../company/service'; // Import CompanyService
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Pagination from '../../Common/Pagination';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import CsvExportModal from '../../shared/components/CsvExportModal';

const UserList = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const [list, setList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [term, setTerm] = useState(''); // Use 'term' instead of 'searchQuery'
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<any | null>(null);
    const [companyList, setCompanyList] = useState<any[]>([]); // State for company options
    const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [showExportModal, setShowExportModal] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const tab = (searchParams.get('tab') as 'all' | 'admins' | 'users') || 'all';

    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '40', value: 40 },
    ];

    const handleTermChange = (e: any) => {
        setTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleEntriesPerPageChange = (selectedOption: any) => {
        setEntriesPerPage(selectedOption ? selectedOption.value : 10);
        setCurrentPage(1);
    };

    const handleCompanyChange = (selectedOption: any) => {
        setSelectedCompany(selectedOption ? selectedOption.value : null);
        setCurrentPage(1);
    };
    const handleRoleChange = (selectedOption: any) => {
        setSelectedRole(selectedOption ? selectedOption.value : null);
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleImpersonate = async (user: any) => {
        try {
            const response = await UserService.impersonate(user.id);
            const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';
            window.open(`${frontendUrl}/impersonate?token=${response.token}`, '_blank');
        } catch (error) {
            ErrorToast(error);
        }
    };

    const handleDeleteClick = (user: any) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (userToDelete !== null) {
            try {
                await UserService.delete(userToDelete.id);
                SuccessToast(t('user.successDelete'));
                setShowDeleteModal(false);
                setUserToDelete(null);
                // Refresh the list after deletion
                fetchData();
            } catch (error) {
                ErrorToast(error);
            }
        }
    };

    const handleTabSelect = (k: string | null) => {
        setSearchParams({ tab: k || 'all' });
    };

    const fetchData = async () => {
        try {
            let roleFilter = selectedRole;

            // Override role filter based on tab selection
            if (tab === 'admins') {
                roleFilter = 'ROLE_ADMIN';
            } else if (tab === 'users') {
                // For users tab, we need to exclude ROLE_ADMIN users
                // We'll handle this by filtering the results after fetching
            }

            const response = await UserService.getAll({
                page: currentPage,
                perPage: entriesPerPage,
                term, // Pass term to backend
                company: selectedCompany, // Pass selected company ID to backend
                role: roleFilter
            });

            let data = response.data;

            // If users tab is selected and no specific role filter, exclude ROLE_ADMIN users
            if (tab === 'users' && !selectedRole) {
                data = data.filter((user: any) => !user.roles?.includes('ROLE_ADMIN'));
            }

            setList(data);
            setTotalPages(response.totalPages);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await CompanyService.getAll({ perPage: 0, page: 1 });
            const companies = response.data.map((company: any) => ({
                label: company.name,
                value: company.id,
            }));
            setCompanyList(companies);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchCompanies(); // Fetch company options on component mount
    }, []);

    useEffect(() => {
        fetchData(); // Fetch users whenever filters or pagination change
    }, [currentPage, entriesPerPage, term, selectedCompany, selectedRole, tab]);

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody className="card-body">
                        <div className='text-end'>
                            <button className='btn btn-success text-end me-2' onClick={() => setShowExportModal(true)}><i
                                className='ti ti-file-export f-18'></i> {t('csvExport.export')}</button>
                            <button className='btn btn-primary text-end me-2' onClick={() => navigate('/users/create')}><i
                                className='ti ti-plus f-18'></i> {t('user.addUser')}</button>
                            <button className='btn btn-primary text-end' onClick={() => navigate('/admins/create')}><i
                                className='ti ti-plus f-18'></i> {t('user.addAdmin')}</button>
                        </div>

                        <div className="mb-3">
                            <Tabs
                                id="user-list-tabs"
                                activeKey={tab}
                                onSelect={handleTabSelect}
                                className="mb-3"
                            >
                                <Tab eventKey="all" title={t('user.all')}>
                                    <UserTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleImpersonate={handleImpersonate}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                        handleRoleChange={handleRoleChange}
                                        selectedRole={selectedRole}
                                    />
                                </Tab>
                                <Tab eventKey="admins" title={t('user.admins')}>
                                    <UserTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleImpersonate={handleImpersonate}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                        handleRoleChange={handleRoleChange}
                                        selectedRole={selectedRole}
                                    />
                                </Tab>
                                <Tab eventKey="users" title={t('user.users')}>
                                    <UserTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleImpersonate={handleImpersonate}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                        handleRoleChange={handleRoleChange}
                                        selectedRole={selectedRole}
                                    />
                                </Tab>
                            </Tabs>
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </CardBody>
                </Card>
                <CsvExportModal
                    show={showExportModal}
                    onHide={() => setShowExportModal(false)}
                    title={t('csvExport.usersTitle')}
                    fileNamePrefix='users'
                    exportCsv={UserService.exportCsv}
                />
            </Col>
            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={userToDelete?.name}
            />
        </React.Fragment>
    );
};

// UserTable component
const UserTable = ({
    list,
    t,
    navigate,
    handleDeleteClick,
    handleImpersonate,
    handleTermChange,
    handleEntriesPerPageChange,
    entriesPerPageOptions,
    entriesPerPage,
    term,
    companyList,
    handleCompanyChange,
    selectedCompany,
    handleRoleChange,
    selectedRole
}: any) => {
    return (
        <>
            <div className="d-sm-flex align-items-center mb-3">
                <ul className="list-inline me-auto my-1">
                    <li className="list-inline-item">
                        <Select
                            placeholder={t('user.entriesPerPage')}
                            options={entriesPerPageOptions}
                            onChange={handleEntriesPerPageChange}
                            defaultValue={entriesPerPageOptions.find((option: any) => option.value === entriesPerPage)}
                            classNamePrefix="react-select"
                        />
                    </li>
                    <span> {t('user.entriesPerPage')}</span>
                </ul>
                <ul className="list-inline ms-auto my-1 mt-3">
                    <li className="list-inline-item">
                        <Select
                            placeholder={t('user.selectUserType')}
                            options={["ROLE_COMPANY_ADMIN", "ROLE_USER", "ROLE_ADMIN", "ROLE_COMPANY_USER"]?.map(userRole => ({
                                label: t(`enums.userRole.${userRole}`),
                                value: userRole
                            }))}
                            onChange={handleRoleChange}
                            value={selectedRole ? { label: t(`enums.userRole.${selectedRole}`), value: selectedRole } : null}
                            isClearable
                            classNamePrefix="react-select"
                        />
                    </li>
                    <li className="list-inline-item">
                        <Select
                            placeholder={t('user.selectCompany')}
                            options={companyList}
                            onChange={handleCompanyChange}
                            value={companyList.find((company: any) => company.value === selectedCompany) || null}
                            isClearable
                            classNamePrefix="react-select"
                        />
                    </li>
                    <li className="list-inline-item">
                        <form className="">
                            <input
                                className='form-control'
                                type="search"
                                placeholder={t('user.searchPlaceholder')}
                                value={term}
                                onChange={handleTermChange}
                            />
                        </form>
                    </li>
                </ul>
            </div>
            <div className="table-responsive">
                <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                    <thead>
                        <tr>
                            <th>{t('user.id')}</th>
                            <th>{t('user.firstName')}</th>
                            <th>{t('user.lastName')}</th>
                            <th>{t('user.email')}</th>
                            <th>{t('user.roles')}</th>
                            <th>{t('user.company')}</th>
                            <th>{t('user.createdAt')}</th>
                            <th>{t('user.updatedAt')}</th>
                            <th className="text-end sticky-col">{t('user.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((item: any, index: number) => (
                            <tr key={index}>
                                <td>{item.id}</td>
                                <td>{item.firstName}</td>
                                <td>{item.lastName}</td>
                                <td>{item.email}</td>
                                <td>{item?.roles?.map((role: string) => t(`enums.userRole.${role}`))?.join(", ")}</td>
                                <td>{item.company?.name ? item.company?.name : t('user.noCompany')}</td>
                                <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                                <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                                <td className="text-end text-nowrap sticky-col">
                                    <ul className="list-inline mb-0">
                                        <li className="list-inline-item"
                                            onClick={() => {
                                                const isAdmin = item.roles?.includes("ROLE_ADMIN");
                                                const editPath = isAdmin ? `/admins/edit/${item.id}` : `/users/edit/${item.id}`;
                                                navigate(editPath);
                                            }}>
                                            <a href="#"
                                                className="avtar avtar-s btn-link-success btn-pc-default">
                                                <i className="ti ti-edit f-20"></i>
                                            </a>
                                        </li>
                                        {!item.roles?.includes("ROLE_ADMIN") && (
                                            <li className="list-inline-item">
                                                <span
                                                    role="button"
                                                    title={t('user.impersonate')}
                                                    className="avtar avtar-s btn-link-warning btn-pc-default"
                                                    onClick={() => handleImpersonate(item)}
                                                >
                                                    <i className="ti ti-login f-20"></i>
                                                </span>
                                            </li>
                                        )}
                                        <li className="list-inline-item">
                                            <span
                                                role="button"
                                                className="avtar avtar-s btn-link-danger btn-pc-default"
                                                onClick={() => handleDeleteClick(item)}
                                            >
                                                <i className="ti ti-trash f-20"></i>
                                            </span>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default UserList;